import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Brain,
  Code2,
  ExternalLink,
  Github,
  Monitor,
  Terminal,
  Zap,
} from 'lucide-react';

const GITHUB_URL = 'https://github.com/Theinventor17/cerebral-os';

const HIGHLIGHTS = [
  {
    icon: Code2,
    title: 'Agents & chat',
    text: 'Multi-session agent chat, composer workflows, tool flows with approvals, and an integrated terminal — all in one IDE-style workspace.',
  },
  {
    icon: Monitor,
    title: 'Your models, your keys',
    text: 'Connect Ollama, LM Studio, llama.cpp, OpenRouter, OpenAI, Anthropic, Gemini, or custom endpoints. Local-first — no cloud backend shipped with the app.',
  },
  {
    icon: Terminal,
    title: 'Full workspace',
    text: 'Open a project folder, browse files, run a terminal, edit in tabs, and drive workspace-scoped actions from the desktop shell.',
  },
  {
    icon: Brain,
    title: 'Thought-assisted control',
    text: 'Manual, Hybrid, or Neural modes. Optional EMOTIV / Cortex headset support for neural input when you want hands-free orchestration.',
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

export default function CerebralOSPage() {
  return (
    <>
      <Helmet>
        <title>Cerebral OS — SELF Labs</title>
        <meta
          name="description"
          content="Cerebral OS is an open-source AI IDE for agent chat, code execution, local models, and thought-assisted control. AGPL-3.0 on GitHub."
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white">
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 75%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 85% 65%, var(--glow-blue) 0%, transparent 45%)',
          }}
        />

        {/* Hero */}
        <section className="relative px-4 pb-16 pt-28 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                <Zap className="h-3.5 w-3.5" />
                Open source · AGPL-3.0
              </span>
              <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                Cerebral OS
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
                An open-source AI IDE that combines chat, code execution, local models, and
                thought-assisted control into one workspace.
              </p>
              <p className="mx-auto mt-4 max-w-xl text-sm text-white/45">
                Build, automate, and run projects with AI using Manual, Hybrid, or Neural modes —
                developed by SELF Labs and available on GitHub.
              </p>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <a
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black shadow-[0_0_24px_var(--glow-purple),0_0_32px_var(--glow-blue)] hover:bg-white/90 transition-transform hover:scale-[1.02]"
                >
                  <Github className="h-4 w-4" />
                  Get it on GitHub
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:border-white/35 hover:bg-white/10 transition-transform hover:scale-[1.02]"
                >
                  Clone &amp; run from source
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Open source */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <motion.div {...fadeUp} className="rounded-2xl border border-white/12 bg-white/[0.03] p-8 sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Open source
              </p>
              <h2 className="mt-3 text-2xl font-bold sm:text-3xl">Free to use, fork, and contribute</h2>
              <p className="mt-6 text-white/65 leading-relaxed">
                Cerebral OS is a standalone desktop app (Electron · React · TypeScript) for
                thought-assisted AI agent orchestration. It does not ship a cloud backend — you
                connect your own local models or API keys. The project is licensed under{' '}
                <span className="text-white/85">GNU Affero General Public License v3.0 (AGPL-3.0)</span>.
              </p>
              <p className="mt-4 text-sm text-white/45">
                Clone the repo, run <code className="rounded bg-white/10 px-1.5 py-0.5 text-white/70">npm install</code>{' '}
                and <code className="rounded bg-white/10 px-1.5 py-0.5 text-white/70">npm run dev</code>{' '}
                to launch the Electron workspace on Windows, macOS, or Linux.
              </p>

              <ul className="mt-8 space-y-3 text-sm text-white/60">
                {[
                  'Multi-session agent chat with composer and tool approvals',
                  'Ollama, LM Studio, OpenRouter, OpenAI, Anthropic, Gemini, and more',
                  'Project folder, file browser, terminal, and editor tabs',
                  'Secure local credential storage — you control which providers run',
                  'Optional EMOTIV / Cortex headset integration for neural input',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <Github className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <a
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 underline underline-offset-4 hover:text-white"
                >
                  github.com/Theinventor17/cerebral-os
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Highlights */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <motion.div {...fadeUp} className="text-center">
              <h2 className="text-2xl font-bold">One workspace for agents, code, and control</h2>
              <p className="mx-auto mt-3 max-w-xl text-white/55">
                Local-first IDE-style environment — powerful enough for automation, private enough
                for sensitive repos.
              </p>
            </motion.div>
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              {HIGHLIGHTS.map((item, i) => (
                <motion.div
                  key={item.title}
                  {...fadeUp}
                  transition={{ delay: i * 0.06 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                >
                  <item.icon className="mb-4 h-7 w-7 text-white/70" strokeWidth={1.5} />
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Modes */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <motion.div {...fadeUp}>
              <h2 className="text-2xl font-bold">Manual · Hybrid · Neural</h2>
              <p className="mx-auto mt-4 max-w-2xl text-white/55 leading-relaxed">
                Cycle session modes from the IDE — keyboard and mouse when you want precision,
                thought-assisted input when you have a compatible headset connected. Local-only
                mode keeps cloud providers off while you work with on-device models.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
            <p className="text-white/55">
              Part of the SELF Labs ecosystem. For the signal-native OS layer, see{' '}
              <a
                href="https://useselfos.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/75 underline underline-offset-2 hover:text-white"
              >
                SELF OS
              </a>
              . For the unified AI API, see{' '}
              <Link to="/resonatia" className="text-white/75 underline underline-offset-2 hover:text-white">
                Resonatia
              </Link>
              .
            </p>
            <a
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-transform hover:scale-[1.02]"
            >
              <Github className="h-4 w-4" />
              Star &amp; download on GitHub
              <ExternalLink className="h-4 w-4" />
            </a>
          </motion.div>
        </section>
      </div>
    </>
  );
}
